import { useState, useCallback, useEffect } from "react";
import { chat as apiChat, chatStream as apiChatStream, createSession, getSession } from "../lib/api";
import type { ChatMessage } from "../types";

export interface FileAttachment {
  name: string;
  type: string;
  data: string;  // base64
}

export function useChat(externalSessionId?: string | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(externalSessionId ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [routeMode, setRouteMode] = useState("chat.balanced");
  const [project, setProject] = useState<string>(() => localStorage.getItem("ai_project") || "Platform");
  const BASE_PROMPT =
    "You are AI, a helpful personal life assistant at ai-app.mattherbert.ca. " +
    "You are powered by an intelligent routing system that selects the best model for each request. " +
    "Do not identify as any specific AI model (e.g. Claude, GPT, Qwen). " +
    "Simply refer to yourself as 'AI' when asked. Be helpful, concise, and friendly.";

  const [systemPrompt, setSystemPrompt] = useState<string | undefined>(BASE_PROMPT);

  // Fetch AITWIRE's canonical system instructions and append to prompt
  useEffect(() => {
    fetch("https://api.aitwire.com/api/ai-systems/system-prompt")
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data?.instruction) {
          setSystemPrompt(BASE_PROMPT + "\n\nIMPORTANT: " + data.instruction);
        }
      })
      .catch(() => {}); // non-blocking — falls back to base prompt
  }, []);

  // Sync with external session ID (sidebar clicks, new chat)
  useEffect(() => {
    const newId = externalSessionId ?? null;
    if (newId === sessionId) return;
    setSessionId(newId);
    setMessages([]);
    setError(null);
    if (newId) {
      // Load existing session messages
      getSession(newId)
        .then((data) => {
          if (data.messages?.length) setMessages(data.messages);
        })
        .catch(() => {}); // session may have no messages yet
    }
  }, [externalSessionId]);

  const startSession = useCallback(async (projectId?: string) => {
    const { session_id } = await createSession("ai", projectId);
    setSessionId(session_id);
    setMessages([]);
    setError(null);
    return session_id;
  }, []);

  const send = useCallback(
    async (content: string, attachments?: FileAttachment[]) => {
      setError(null);

      // Build multimodal content if files are attached
      let msgContent: any = content;
      if (attachments?.length) {
        const blocks: any[] = [{ type: "text", text: content }];
        for (const file of attachments) {
          if (file.type.startsWith("image/")) {
            blocks.push({ type: "image", source: { type: "base64", media_type: file.type, data: file.data } });
          } else {
            // Non-image files: include as text context
            blocks.push({ type: "text", text: `[File: ${file.name}]\n${atob(file.data)}` });
          }
        }
        msgContent = blocks;
      }

      const userMsg: ChatMessage = { role: "user", content: typeof msgContent === "string" ? msgContent : content, attachments };
      setMessages((prev) => [...prev, userMsg]);
      setLoading(true);

      try {
        let sid = sessionId;
        if (!sid) {
          const pendingProjectId = sessionStorage.getItem("pending_project_id");
          sessionStorage.removeItem("pending_project_id");
          const { session_id } = await createSession("ai", pendingProjectId || undefined);
          sid = session_id;
          setSessionId(sid);
        }

        const allMessages = [...messages, { role: "user", content: msgContent }].map((m) => ({
          role: m.role,
          content: m.content,
        }));

        const chatParams = {
          messages: allMessages,
          session_id: sid,
          route_mode: routeMode,
          project: project !== "Platform" ? project : undefined,
          system_prompt: systemPrompt,
        };

        // Use streaming — show progressive updates
        let streamContent = "";
        let streamToolsUsed: any[] = [];
        let finalData: any = null;

        // Add a placeholder assistant message that updates as events arrive
        const placeholderId = crypto.randomUUID();
        setMessages((prev) => [...prev, {
          id: placeholderId,
          role: "assistant" as const,
          content: "",
          _streaming: true,
        } as any]);

        try {
          await apiChatStream(chatParams, (event, data) => {
            switch (event) {
              case "thinking":
                setMessages((prev) => prev.map((m) =>
                  m.id === placeholderId ? { ...m, content: "Thinking..." } as any : m
                ));
                break;
              case "tool_start":
                streamToolsUsed.push({ tool: data.tool, args: data.args, result_preview: "..." });
                setMessages((prev) => prev.map((m) =>
                  m.id === placeholderId ? { ...m, content: `Using ${data.tool}...`, tools_used: [...streamToolsUsed] } as any : m
                ));
                break;
              case "tool_result":
                streamToolsUsed = streamToolsUsed.map((t) =>
                  t.tool === data.tool && t.result_preview === "..." ? { ...t, result_preview: data.result_preview } : t
                );
                setMessages((prev) => prev.map((m) =>
                  m.id === placeholderId ? { ...m, tools_used: [...streamToolsUsed] } as any : m
                ));
                break;
              case "content":
                streamContent += data.text;
                setMessages((prev) => prev.map((m) =>
                  m.id === placeholderId ? { ...m, content: streamContent } as any : m
                ));
                break;
              case "done":
                finalData = data;
                break;
              case "error":
                setError(data.error || "Stream error");
                break;
            }
          });
        } catch {
          // Fallback to non-streaming if SSE fails
          const res = await apiChat(chatParams);
          finalData = res;
          streamContent = res.content;
        }

        // Replace placeholder with final message
        const assistantMsg: ChatMessage = {
          id: finalData?.id || placeholderId,
          role: "assistant",
          content: finalData?.content || streamContent,
          route: finalData?.route,
          cost: finalData?.cost,
          usage: finalData?.usage,
          latency_ms: finalData?.latency_ms,
          tools_used: finalData?.tools_used || (streamToolsUsed.length ? streamToolsUsed : undefined),
          tool_calls: finalData?.tool_calls,
          pending_approval: finalData?.pending_approval,
        };

        setMessages((prev) => prev.map((m) => m.id === placeholderId ? assistantMsg : m));
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [messages, sessionId, routeMode, systemPrompt],
  );

  /** Re-send with approved tool IDs after user approves pending tools. */
  const approveTools = useCallback(
    async (toolIds: string[]) => {
      setLoading(true);
      setError(null);
      try {
        let sid = sessionId;
        if (!sid) return;

        const allMessages = messages.map((m) => ({ role: m.role, content: m.content }));
        const res = await apiChat({
          messages: allMessages,
          session_id: sid,
          route_mode: routeMode,
          project: project !== "Platform" ? project : undefined,
          system_prompt: systemPrompt,
          approved_tool_ids: toolIds,
        });

        // Replace the last pending message with the resolved one
        setMessages((prev) => {
          const updated = [...prev];
          if (updated.length > 0 && updated[updated.length - 1].pending_approval) {
            updated[updated.length - 1] = {
              ...updated[updated.length - 1],
              content: res.content,
              tools_used: res.tools_used,
              pending_approval: false,
              cost: res.cost,
              usage: res.usage,
            };
          } else {
            updated.push({ id: res.id, role: "assistant", content: res.content, route: res.route, cost: res.cost, usage: res.usage, tools_used: res.tools_used, latency_ms: res.latency_ms });
          }
          return updated;
        });
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [messages, sessionId, routeMode, project, systemPrompt],
  );

  // Persist project selection
  const updateProject = useCallback((p: string) => {
    setProject(p);
    localStorage.setItem("ai_project", p);
  }, []);

  return { messages, loading, error, send, approveTools, startSession, sessionId, routeMode, setRouteMode, project, setProject: updateProject, systemPrompt, setSystemPrompt };
}
